import {
  useLocalStorageUpdateCallback,
  useLocalStorageValueCallback,
} from '@aboutyou/storage';
import {useCallback} from 'react';

type AbTestState = 'pending' | 'activated';
interface Experiment {
  name: string;
  value: string;
}

const pendingAbTestsActivations = new Map<string, boolean>();

interface ActivatedAbTest {
  [key: string]: string;
}

const ACTIVATED_AB_TEST_DEFAULT_VALUE = {};
const useActivatedAbTestStorage = () => {
  const getActivatedAbTests = useLocalStorageValueCallback<ActivatedAbTest>({
    key: 'ay-activated-ab-tests',
    defaultValue: ACTIVATED_AB_TEST_DEFAULT_VALUE,
  });

  const setActivatedAbTests = useLocalStorageUpdateCallback<ActivatedAbTest>({
    key: 'ay-activated-ab-tests',
  });

  return {
    getActivatedAbTests,
    setActivatedAbTests,
  };
};

export const useActivatedAbTests = () => {
  const {getActivatedAbTests, setActivatedAbTests} =
    useActivatedAbTestStorage();

  const isExperimentActivated = useCallback(
    (experiment: Experiment) => {
      if (pendingAbTestsActivations.get(experiment.name)) {
        return true;
      }
      const activatedAbTests = getActivatedAbTests() as ActivatedAbTest;

      return activatedAbTests?.[experiment.name] === experiment.value;
    },
    [getActivatedAbTests],
  );

  const setTestStateForUserId = useCallback(
    (experiment: Experiment, state: AbTestState) => {
      if (state === 'pending') {
        pendingAbTestsActivations.set(experiment.name, true);
        // We don't want to store the pending state to the local storage, therefor we are bailing early
        return;
      }

      setActivatedAbTests(activatedAbTests => {
        const existingAbTests = activatedAbTests ?? getActivatedAbTests() ?? {};
        return {
          ...existingAbTests,
          [experiment.name]: experiment.value,
        };
      });
    },
    [getActivatedAbTests, setActivatedAbTests],
  );

  return {
    isExperimentActivated,
    setTestStateForUserId,
  };
};
